import React, { useEffect, useState, useCallback } from 'react';
import { Container, Button, ProgressBar, Form, Row, Col, ListGroup, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSettings } from '../settings/SettingsContext';
import { useTenant } from '../settings/TenantContext';
import { supabase } from '../lib/supabaseClient';

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com','yahoo.com','outlook.com','hotmail.com','icloud.com','aol.com',
  'protonmail.com','proton.me','mail.com','zoho.com','yandex.com','live.com',
  'msn.com','me.com','mac.com','inbox.com','fastmail.com','tutanota.com','gmx.com','gmx.net',
]);

// Step keys used to build the dynamic step array
const STEP_CONFIRM_URL = 'confirm_url';
const STEP_TENANT_CONFLICT = 'tenant_conflict';
const STEP_STARTING_URL = 'starting_url';
const STEP_DO_NOT_CRAWL = 'do_not_crawl';
const STEP_CRITICAL_INSTRUCTIONS = 'critical_instructions';
const STEP_TONE = 'tone';
const STEP_PRIMARY_FACTS = 'primary_facts';
const STEP_NAME = 'name';
const STEP_ALMOST_READY = 'almost_ready';

const STEP_LABELS = {
  [STEP_CONFIRM_URL]: 'Confirm URL',
  [STEP_TENANT_CONFLICT]: 'Existing Account',
  [STEP_STARTING_URL]: 'Starting URL',
  [STEP_DO_NOT_CRAWL]: 'Do Not Crawl',
  [STEP_CRITICAL_INSTRUCTIONS]: 'Critical Instructions',
  [STEP_TONE]: 'Tone',
  [STEP_PRIMARY_FACTS]: 'Primary Facts',
  [STEP_NAME]: 'Name',
  [STEP_ALMOST_READY]: 'Almost Ready',
};

const DEFAULT_CRITICAL_INSTRUCTIONS =
  'Deflect politely controversial or polarizing political, sensitive health/medical and financial topics.\n' +
  'Answer thoroughly but briefly.\n' +
  'Keep the user engaged by asking follow-up questions.';

const DEFAULT_TONE =
  'Professional yet approachable,\n' +
  'Confident and authoritative,\n' +
  'Energetic and benefit-driven';

function GettingStarted() {
  const { user, setIsConfigured } = useAuth();
  const { setTenantId: setSelectedTenantId, refreshMemberships } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  // If this wizard was reached via an invitation flow, redirect to dashboard
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      if (params.get('invited') === '1') {
        navigate('/dashboard');
      }
    } catch (_) { /* ignore */ }
  }, [location.search, navigate]);

  // Set robots noindex meta tag
  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex');
    return () => {
      if (metaRobots && metaRobots.parentNode) {
        metaRobots.parentNode.removeChild(metaRobots);
      }
    };
  }, []);

  // Fallback: if user has pending/auto-accepted invites, redirect to dashboard
  useEffect(() => {
    (async () => {
      try {
        let session = (await supabase.auth.getSession()).data.session;
        for (let i = 0; !session && i < 20; i++) { await new Promise(r=>setTimeout(r,150)); session = (await supabase.auth.getSession()).data.session; }
        const token = session?.access_token || '';
        if (!token) return;
        const base = process.env.REACT_APP_SKINNY_SUPABASE_URL;
        const headers = { 'content-type': 'application/json', Authorization: `Bearer ${token}` };
        try { await fetch(`${base}/functions/v1/invites-auto-accept`, { method: 'POST', headers, body: '{}' }); } catch (_) { /* ignore */ }
        try {
          const res = await fetch(`${base}/functions/v1/user-memberships`, { method: 'POST', headers, body: '{}' });
          if (res.ok) {
            const j = await res.json();
            const list = Array.isArray(j.memberships) ? j.memberships : [];
            const hasCompleted = list.some((m) => m.has_onboarded);
            if (hasCompleted) navigate('/dashboard', { replace: true });
          }
        } catch (_) { /* ignore */ }
      } catch (_) { /* ignore */ }
    })();
  }, [navigate]);

  const {
    siteUrl, setSiteUrl,
    startingUrl, setStartingUrl,
    doNotCrawlUrls, addDoNotCrawl, updateDoNotCrawl, removeDoNotCrawl,
    criticalInstructions, setCriticalInstructions,
    tone, setTone,
    primaryFacts, setPrimaryFacts,
    skinnyName, setSkinnyName,
    useSitemapSeed, setUseSitemapSeed,
    sitemapUrl, setSitemapUrl,
  } = useSettings();

  // Pre-populate critical instructions & tone with defaults when empty
  useEffect(() => {
    if (!criticalInstructions) setCriticalInstructions(DEFAULT_CRITICAL_INSTRUCTIONS);
    if (!tone) setTone(DEFAULT_TONE);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dynamic step management ──────────────────────────────────────────
  const [showTenantConflict, setShowTenantConflict] = useState(false);
  const [tenantConflictMessage, setTenantConflictMessage] = useState('');
  const [checkingTenant, setCheckingTenant] = useState(false);

  const buildSteps = useCallback(() => {
    const s = [STEP_CONFIRM_URL];
    if (showTenantConflict) s.push(STEP_TENANT_CONFLICT);
    s.push(STEP_STARTING_URL, STEP_DO_NOT_CRAWL, STEP_CRITICAL_INSTRUCTIONS, STEP_TONE, STEP_PRIMARY_FACTS, STEP_NAME, STEP_ALMOST_READY);
    return s;
  }, [showTenantConflict]);

  const steps = buildSteps();
  const stepLabels = steps.map(k => STEP_LABELS[k]);

  const [stepIdx, setStepIdx] = useState(0);
  const currentStepKey = steps[stepIdx] || STEP_CONFIRM_URL;
  const progress = Math.round(((stepIdx + 1) / steps.length) * 100);

  const [confirmInput, setConfirmInput] = useState('');
  const [newExclude, setNewExclude] = useState('');
  const [tenantId, setTenantId] = useState('TENANT_ID');
  const [nameInput, setNameInput] = useState(skinnyName || '');
  const [saving, setSaving] = useState(false);
  const [generatingFacts, setGeneratingFacts] = useState(false);
  const [generateFactsError, setGenerateFactsError] = useState('');

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="wiz-tip" className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  useEffect(() => {
    async function fetchTenant() {
      if (!user?.id) return;
      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token || '';
        const res = await fetch(`${process.env.REACT_APP_SKINNY_SUPABASE_URL}/functions/v1/settings-get`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const j = await res.json();
          if (j && j.tenant_id) setTenantId(j.tenant_id);
        }
      } catch (_) { /* ignore */ }
    }
    fetchTenant();
  }, [user]);

  // Guard: ensure siteUrl matches email domain and not example.com when step 0 loads
  const [initializedUrl, setInitializedUrl] = useState(false);
  useEffect(() => {
    if (initializedUrl) return;
    if (!user?.email) return;
    try {
      const match = user.email.toLowerCase().match(/@([^@\s>]+)$/);
      const domain = match && match[1] ? match[1] : '';
      const isFreeEmail = !domain || FREE_EMAIL_DOMAINS.has(domain);
      if (isFreeEmail) {
        const isUnset = !siteUrl || String(siteUrl || '').trim() === '';
        const isExample = String(siteUrl || '').includes('example.com');
        const isFreeUrl = domain && String(siteUrl || '').includes(domain);
        if (isUnset || isExample || isFreeUrl) {
          setSiteUrl('');
          setStartingUrl('');
        }
      } else {
        const expected = `https://${domain}`;
        const isUnset = !siteUrl || String(siteUrl || '').trim() === '';
        const isExample = String(siteUrl || '').includes('example.com');
        if (isUnset || isExample) {
          setSiteUrl(expected);
          setStartingUrl(expected);
        }
      }
    } catch (_) { /* ignore */ }
    setInitializedUrl(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializedUrl, user?.email, siteUrl, setSiteUrl, setStartingUrl]);

  // ── Tenant-check after URL confirmation ──────────────────────────────
  async function checkTenantConflict() {
    try {
      setCheckingTenant(true);
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${process.env.REACT_APP_SKINNY_SUPABASE_URL}/functions/v1/tenant-check`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}`, apikey: process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY },
        body: JSON.stringify({ siteUrl }),
      });
      if (res.ok) {
        const j = await res.json();
        if (j && j.match) {
          setTenantConflictMessage(j.message || 'An account already exists for this website.');
          setShowTenantConflict(true);
          setCheckingTenant(false);
          return true; // conflict found
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[GettingStarted] tenant-check error:', err);
    }
    setShowTenantConflict(false);
    setCheckingTenant(false);
    return false; // no conflict
  }

  // ── AI Generate Primary Facts ────────────────────────────────────────
  async function handleGeneratePrimaryFacts() {
    const urlToAnalyze = startingUrl?.trim() || siteUrl?.trim();
    if (!urlToAnalyze) {
      setGenerateFactsError('A Starting URL or Site URL is required to generate facts.');
      return;
    }
    if (primaryFacts && primaryFacts.trim()) {
      if (!window.confirm('This will replace your current Primary Facts. Continue?')) return;
    }
    try {
      setGeneratingFacts(true);
      setGenerateFactsError('');
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${process.env.REACT_APP_SKINNY_SUPABASE_URL}/functions/v1/generate-primary-facts`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}`, apikey: process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY },
        body: JSON.stringify({ siteUrl: urlToAnalyze }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error('[GettingStarted] generate-primary-facts failed:', err);
        setGenerateFactsError('Failed to generate facts. Please try again or enter them manually.');
        return;
      }
      const j = await res.json();
      if (j.primaryFacts) {
        setPrimaryFacts(j.primaryFacts);
      } else {
        setGenerateFactsError('No content was generated. Please try again or enter facts manually.');
      }
    } catch (e) {
      console.error('[GettingStarted] generate-primary-facts error:', e);
      setGenerateFactsError('An error occurred. Please try again or enter facts manually.');
    } finally {
      setGeneratingFacts(false);
    }
  }

  // ── Navigation ───────────────────────────────────────────────────────
  async function next() {
    // URL confirmation step
    if (currentStepKey === STEP_CONFIRM_URL) {
      if (!siteUrl || !siteUrl.trim()) {
        alert('Please enter your site URL.');
        return;
      }
      if (!confirmInput || confirmInput.trim() !== siteUrl.trim()) {
        alert('The URLs do not match. If this is incorrect, please contact support.');
        return;
      }
      // Also set startingUrl to siteUrl if startingUrl is empty
      if (!startingUrl || !startingUrl.trim()) {
        setStartingUrl(siteUrl);
      }
      // Check for tenant conflict
      const hasConflict = await checkTenantConflict();
      if (hasConflict) {
        // Move to the conflict interstitial (it was just inserted as step index 1)
        setStepIdx(1);
        return;
      }
    }

    // Name step — persist settings via onboard-user
    if (currentStepKey === STEP_NAME) {
      try {
        setSaving(true);
        if (nameInput) setSkinnyName(nameInput);
        const payload = {
          siteUrl,
          startingUrl,
          doNotCrawlUrls,
          criticalInstructions,
          tone,
          primaryFacts,
          skinnyName: nameInput,
          useSitemapSeed: !!useSitemapSeed,
          sitemapUrl: sitemapUrl || null,
        };
        const token = (await supabase.auth.getSession()).data.session?.access_token || '';
        const res = await fetch(`${process.env.REACT_APP_SKINNY_SUPABASE_URL}/functions/v1/onboard-user`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}`, apikey: process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY },
          body: JSON.stringify(payload),
        });
        const txt = await res.text();
        if (!res.ok) {
          console.error('[GettingStarted] onboard-user failed on Name step Next:', txt);
          alert(`Failed to save settings: ${txt}`);
          setSaving(false);
          return;
        }
        let json = {};
        try { json = JSON.parse(txt); } catch(_) {}
        let resolvedTenantId = tenantId;
        let resolvedSiteId = '';
        if (json && json.tenant_id) {
          setTenantId(json.tenant_id);
          resolvedTenantId = json.tenant_id;
          try {
            setSelectedTenantId(json.tenant_id);
            await refreshMemberships();
          } catch (_) { /* ignore */ }
        }
        if (json && json.site_id) {
          resolvedSiteId = json.site_id;
        }
        if (resolvedTenantId && resolvedTenantId !== 'TENANT_ID' && resolvedSiteId && typeof window !== 'undefined') {
          try { localStorage.setItem(`site_sel_${resolvedTenantId}`, resolvedSiteId); } catch (_) {}
        }
        if (resolvedTenantId && resolvedTenantId !== 'TENANT_ID') {
          try {
            await supabase.functions.invoke('user-onboarding-complete', { body: { tenant_id: resolvedTenantId } });
          } catch (_) { /* ignore */ }
        }
        setIsConfigured(true);
      } catch (e) {
        console.error('[GettingStarted] onboard-user error:', e);
        alert(`Error saving settings: ${e.message || String(e)}`);
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    setStepIdx(s => Math.min(s + 1, steps.length - 1));
  }

  function prev() {
    if (currentStepKey === STEP_TENANT_CONFLICT) {
      // Go back to URL confirmation and clear the conflict
      setShowTenantConflict(false);
      setStepIdx(0);
      return;
    }
    setStepIdx(s => Math.max(s - 1, 0));
  }

  function handleConflictCreateNew() {
    // User chose to create a new account — dismiss the interstitial and advance
    setShowTenantConflict(false);
    // After clearing the conflict, the step array shrinks. The next step after
    // STEP_CONFIRM_URL is now STEP_STARTING_URL (index 1).
    setStepIdx(1);
  }

  return (
    <Container className="py-5" style={{ maxWidth: 900 }}>
      <h1 className="h3 fw-bold mb-3">Getting Started</h1>
      <ProgressBar now={progress} label={`${progress}%`} className="mb-3" />
      <div className="mb-2 text-muted">Step {stepIdx + 1} of {steps.length}: {stepLabels[stepIdx]}</div>

      {currentStepKey === STEP_CONFIRM_URL && (
        <div>
          <Form.Group className="mb-3">
            <Form.Label>Your site URL <span className="text-muted">(e.g., https://yourdomain.com)</span></Form.Label>
            <Form.Control type="url" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://yourdomain.com" />
            <div className="form-text">After changing your Site URL, you must request a new crawl from the Dashboard.</div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Type the URL exactly as shown above to confirm</Form.Label>
            <Form.Control type="url" value={confirmInput} onChange={(e) => setConfirmInput(e.target.value)} placeholder="" />
          </Form.Group>
        </div>
      )}

      {currentStepKey === STEP_TENANT_CONFLICT && (
        <div className="py-4">
          <Alert variant="warning" className="mb-4">
            <Alert.Heading className="h5">An existing account was found</Alert.Heading>
            <p className="mb-0">
              {tenantConflictMessage || 'An account already exists for this website.'}
              {' '}If you believe you should have access, please contact your organization's
              administrator to request an invitation.
            </p>
          </Alert>
          <div className="d-flex gap-3">
            <Button variant="primary" onClick={handleConflictCreateNew}>
              Create a new, separate account
            </Button>
            <Button variant="outline-secondary" onClick={prev}>
              Go back and change URL
            </Button>
          </div>
        </div>
      )}

      {currentStepKey === STEP_STARTING_URL && (
        <div>
          <Form.Group>
            <Form.Label>
              Starting URL
              <InfoIcon text="The first page to crawl. Usually your homepage or a docs/FAQ index. You can change this later." />
            </Form.Label>
            <Form.Control type="url" value={startingUrl} onChange={(e) => setStartingUrl(e.target.value)} />
            <Form.Check className="mt-2" type="checkbox" id="use-sitemap-seed"
              label={
                <>
                  Use sitemap.xml as initial seed
                  <InfoIcon text="When checked, we start with the pages listed in sitemap.xml, If we don't find this file on your site, we start with the Starting URL. If sitemap.xml does not contain your starting URL, we will add both to our crawl." />
                </>
              }
              checked={!!useSitemapSeed}
              onChange={(e) => setUseSitemapSeed(e.target.checked)}
            />
            <Form.Control className="mt-2" type="url" placeholder="Custom sitemap URL (optional)" value={sitemapUrl || ''} onChange={(e)=> setSitemapUrl(e.target.value)} />
          </Form.Group>
        </div>
      )}

      {currentStepKey === STEP_DO_NOT_CRAWL && (
        <div>
          <Form.Label>
            Do Not Crawl URLs
            <InfoIcon text="Exclude paths or full URLs from discovery/crawling (e.g., /admin, /privacy). One per line as you add them." />
          </Form.Label>
          <Row className="g-2 align-items-center mb-2">
            <Col>
              <Form.Control type="url" placeholder="https://example.com/path" value={newExclude} onChange={(e) => setNewExclude(e.target.value)} />
            </Col>
            <Col xs="auto">
              <Button onClick={() => { if (newExclude.trim()) { addDoNotCrawl(newExclude.trim()); setNewExclude(''); } }}>Add</Button>
            </Col>
          </Row>
          <ListGroup>
            {doNotCrawlUrls.map((u, i) => (
              <ListGroup.Item key={`${u}-${i}`} className="d-flex align-items-center justify-content-between">
                <Form.Control value={u} onChange={(e) => updateDoNotCrawl(i, e.target.value)} />
                <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => removeDoNotCrawl(i)}>Remove</Button>
              </ListGroup.Item>
            ))}
            {doNotCrawlUrls.length === 0 && (
              <ListGroup.Item className="text-muted">No exclusions</ListGroup.Item>
            )}
          </ListGroup>
        </div>
      )}

      {currentStepKey === STEP_CRITICAL_INSTRUCTIONS && (
        <Form.Group>
          <Form.Label>
            Critical Instructions
            <InfoIcon text="Non-negotiable guidance the assistant must follow (e.g., Never give medical or legal advice)." />
          </Form.Label>
          <Form.Control as="textarea" rows={5} value={criticalInstructions} onChange={(e) => setCriticalInstructions(e.target.value)} />
        </Form.Group>
      )}

      {currentStepKey === STEP_TONE && (
        <Form.Group>
          <Form.Label>
            Tone
            <InfoIcon text="Desired voice and style (e.g., professional, friendly, concise)." />
          </Form.Label>
          <Form.Control as="textarea" rows={4} value={tone} onChange={(e) => setTone(e.target.value)} />
        </Form.Group>
      )}

      {currentStepKey === STEP_PRIMARY_FACTS && (
        <Form.Group>
          <Form.Label>
            Primary Facts
            <InfoIcon text="Key facts the assistant can rely on (e.g., product details, pricing ranges, policies)." />
            <Button
              variant="outline-primary"
              size="sm"
              className="ms-2"
              disabled={generatingFacts}
              onClick={handleGeneratePrimaryFacts}
            >
              {generatingFacts ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Generating…
                </>
              ) : (
                <>
                  <i className="bi bi-stars me-1" aria-hidden="true"></i>
                  AI Generate
                </>
              )}
            </Button>
          </Form.Label>
          {generatingFacts && (
            <div className="text-muted small mb-2">
              Analyzing your website — this may take 10–15 seconds…
            </div>
          )}
          {generateFactsError && (
            <Alert variant="warning" className="py-2 mb-2" dismissible onClose={() => setGenerateFactsError('')}>
              {generateFactsError}
            </Alert>
          )}
          <Form.Control as="textarea" rows={6} value={primaryFacts} onChange={(e) => setPrimaryFacts(e.target.value)} disabled={generatingFacts} />
        </Form.Group>
      )}

      {currentStepKey === STEP_NAME && (
        <Form.Group>
          <Form.Label>Name your skinny. This will be the text displayed on the Skinny on your pages.</Form.Label>
          <Form.Control value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="e.g., Ask Skinny" />
        </Form.Group>
      )}

      {currentStepKey === STEP_ALMOST_READY && (
        <div className="text-center py-4 position-relative">
          <div style={{ fontSize: 48 }} aria-hidden="true">🎉🥳🎉</div>
          <h2 className="h4 fw-semibold mt-3">Congratulations!</h2>
          <p className="mt-3">
            Your Skinny is mostly set up. Next we need to read your site.
            When you're ready, click <strong>"Crawl My Site"</strong> on the next page to start the first crawl.
          </p>
          <p className="text-muted">
            You can find the embed script and other setup details later in your dashboard under "My Skinny".
          </p>
        </div>
      )}

      {/* Navigation buttons — hidden on the conflict interstitial (it has its own buttons) */}
      {currentStepKey !== STEP_TENANT_CONFLICT && (
        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={prev} disabled={stepIdx === 0}>Previous</Button>
          <div className="d-flex gap-2">
            {stepIdx < steps.length - 1 && (
              <Button variant="primary" onClick={next} disabled={saving || checkingTenant}>
                {saving ? 'Saving…' : checkingTenant ? 'Checking…' : 'Next'}
              </Button>
            )}
            {stepIdx === steps.length - 1 && (
              <Button variant="success" onClick={() => navigate('/dashboard?tab=myskinny')}>Next: My Skinny</Button>
            )}
          </div>
        </div>
      )}
    </Container>
  );
}

export default GettingStarted;
